import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';

import { ThemeProvider } from 'styled-components';
import { AppShell, Main } from './styles/appShell';
import GlobalStyle from './styles/global';
import defaultTheme from './styles/colors/default';
import lightTheme from './styles/colors/light';

import AppProvider from './hooks';
import { PostProvider } from './hooks/post';

import Routes from './routes';
import MouseParticles from './components/MouseParticles';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

type ThemeMode = 'dark' | 'light';

const GUEST_ROUTES = ['/login', '/signup', '/forgot', '/password/reset', '/verify-email', '/resend-verify-email'];

const Shell: React.FC<{
  themeMode: ThemeMode;
  onToggleTheme(): void;
}> = ({ themeMode, onToggleTheme }) => {
  const location = useLocation();

  const isGuestRoute = useMemo(() => {
    return GUEST_ROUTES.some(
      route => location.pathname === route || location.pathname.startsWith(`${route}/`),
    );
  }, [location.pathname]);

  return (
    <>
      <ScrollToTop />
      <MouseParticles />
      <AppShell>
        {!isGuestRoute && <Header themeMode={themeMode} onToggleTheme={onToggleTheme} />}
        <Main>
          <Routes />
        </Main>
        {!isGuestRoute && <Footer />}
      </AppShell>
    </>
  );
};

const App: React.FC = () => {
  const [themeMode, setThemeMode] = useState<ThemeMode>('dark');

  useEffect(() => {
    const storedTheme = localStorage.getItem(
      '@AdopetCP:theme',
    ) as ThemeMode | null;

    if (storedTheme === 'light' || storedTheme === 'dark') {
      setThemeMode(storedTheme);
      document.body.setAttribute('data-theme', storedTheme);
      return;
    }

    document.body.setAttribute('data-theme', 'dark');
  }, []);

  const handleToggleTheme = useCallback(() => {
    setThemeMode(previousTheme => {
      const nextTheme = previousTheme === 'dark' ? 'light' : 'dark';

      localStorage.setItem('@AdopetCP:theme', nextTheme);
      document.body.setAttribute('data-theme', nextTheme);

      return nextTheme;
    });
  }, []);

  const selectedTheme = useMemo(() => {
    return themeMode === 'light' ? lightTheme : defaultTheme;
  }, [themeMode]);

  return (
    <>
      <AppProvider>
        <ThemeProvider theme={selectedTheme}>
          <BrowserRouter>
            <PostProvider>
              <Shell themeMode={themeMode} onToggleTheme={handleToggleTheme} />
            </PostProvider>
          </BrowserRouter>
        </ThemeProvider>
      </AppProvider>
      <GlobalStyle />
    </>
  );
};

export default App;
